var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { useState, useCallback, useEffect, useMemo } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { useOkapiKy } from "@folio/stripes/core";
import { useQuery } from "react-query";
import { fetchAuditLogs } from "../api/auditLogService";
var PAGE_SIZE = 50;
var useAuditLogList = function () {
    var ky = useOkapiKy();
    var location = useLocation();
    var history = useHistory();
    // Default dates: last 7 days
    var defaultDateFrom = useMemo(function () {
        return new Date(Date.now() - 7 * 86400000).toISOString().split("T")[0];
    }, []);
    var defaultDateTo = useMemo(function () {
        return new Date().toISOString().split("T")[0];
    }, []);
    // Parse initial values from URL
    var initialParams = useMemo(function () {
        var params = new URLSearchParams(location.search);
        return {
            query: params.get("q") || "",
            actionType: params.get("actionType") || "",
            entityType: params.get("entityType") || "",
            userId: params.get("userId") || "",
            dateFrom: params.get("dateFrom") || defaultDateFrom,
            dateTo: params.get("dateTo") || defaultDateTo,
            page: parseInt(params.get("page") || "1", 10) || 1,
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    // Pending state (editable)
    var _a = useState(initialParams.query), query = _a[0], setQuery = _a[1];
    var _b = useState(initialParams.actionType), actionType = _b[0], setActionType = _b[1];
    var _c = useState(initialParams.entityType), entityType = _c[0], setEntityType = _c[1];
    var _d = useState(initialParams.userId), userId = _d[0], setUserId = _d[1];
    var _e = useState(""), userName = _e[0], setUserName = _e[1]; // display name for autocomplete
    var _f = useState(initialParams.dateFrom), dateFrom = _f[0], setDateFrom = _f[1];
    var _g = useState(initialParams.dateTo), dateTo = _g[0], setDateTo = _g[1];
    // Applied state (sent to API)
    var _h = useState(initialParams.query), appliedQuery = _h[0], setAppliedQuery = _h[1];
    var _j = useState(initialParams.actionType), appliedActionType = _j[0], setAppliedActionType = _j[1];
    var _k = useState(initialParams.entityType), appliedEntityType = _k[0], setAppliedEntityType = _k[1];
    var _l = useState(initialParams.userId), appliedUserId = _l[0], setAppliedUserId = _l[1];
    var _m = useState(initialParams.dateFrom), appliedDateFrom = _m[0], setAppliedDateFrom = _m[1];
    var _o = useState(initialParams.dateTo), appliedDateTo = _o[0], setAppliedDateTo = _o[1];
    var _p = useState(initialParams.page), page = _p[0], setPage = _p[1];
    var _q = useState(true), filterPaneIsVisible = _q[0], setFilterPaneIsVisible = _q[1];
    var dateValidationError = useMemo(function () {
        return dateFrom && dateTo && dateFrom > dateTo
            ? "ui-czna-users.auditLog.filter.dateError"
            : "";
    }, [dateFrom, dateTo]);
    var handleSearch = useCallback(function (e) {
        e.preventDefault();
        if (dateFrom && dateTo && dateFrom > dateTo)
            return;
        setAppliedQuery(query);
        setAppliedActionType(actionType);
        setAppliedEntityType(entityType);
        setAppliedUserId(userId);
        setAppliedDateFrom(dateFrom);
        setAppliedDateTo(dateTo);
        setPage(1);
    }, [query, actionType, entityType, userId, dateFrom, dateTo]);
    var handleClearSearch = useCallback(function () {
        setQuery("");
        setAppliedQuery("");
        setPage(1);
    }, []);
    var handleClearFilters = useCallback(function () {
        setQuery("");
        setAppliedQuery("");
        setActionType("");
        setAppliedActionType("");
        setEntityType("");
        setAppliedEntityType("");
        setUserId("");
        setAppliedUserId("");
        setUserName("");
        setDateFrom(defaultDateFrom);
        setAppliedDateFrom(defaultDateFrom);
        setDateTo(defaultDateTo);
        setAppliedDateTo(defaultDateTo);
        setPage(1);
    }, [defaultDateFrom, defaultDateTo]);
    var isDefaultFilters = appliedQuery === "" &&
        appliedActionType === "" &&
        appliedEntityType === "" &&
        appliedUserId === "" &&
        appliedDateFrom === defaultDateFrom &&
        appliedDateTo === defaultDateTo;
    // URL sync
    var basePath = useMemo(function () { return location.pathname.replace(/\/[0-9a-f-]{36}$/, ""); }, []);
    useEffect(function () {
        var params = new URLSearchParams();
        if (appliedQuery)
            params.set("q", appliedQuery);
        if (appliedActionType)
            params.set("actionType", appliedActionType);
        if (appliedEntityType)
            params.set("entityType", appliedEntityType);
        if (appliedUserId)
            params.set("userId", appliedUserId);
        if (appliedDateFrom !== defaultDateFrom)
            params.set("dateFrom", appliedDateFrom);
        if (appliedDateTo !== defaultDateTo)
            params.set("dateTo", appliedDateTo);
        if (page > 1)
            params.set("page", page.toString());
        var search = params.toString();
        history.replace({
            pathname: basePath,
            search: search ? "?".concat(search) : "",
        });
    }, [
        appliedQuery,
        appliedActionType,
        appliedEntityType,
        appliedUserId,
        appliedDateFrom,
        appliedDateTo,
        page,
        history,
        basePath,
        defaultDateFrom,
        defaultDateTo,
    ]);
    // Memoize applied filters (used by API call and export)
    var appliedFilters = useMemo(function () { return ({
        q: appliedQuery || undefined,
        actionType: appliedActionType || undefined,
        entityType: appliedEntityType || undefined,
        userId: appliedUserId || undefined,
        dateFrom: appliedDateFrom ? "".concat(appliedDateFrom, "T00:00:00Z") : undefined,
        dateTo: appliedDateTo ? "".concat(appliedDateTo, "T23:59:59Z") : undefined,
    }); }, [
        appliedQuery,
        appliedActionType,
        appliedEntityType,
        appliedUserId,
        appliedDateFrom,
        appliedDateTo,
    ]);
    // API call
    var _r = useQuery(["czna-users", "audit-logs", appliedFilters, page], function () {
        return fetchAuditLogs(ky, __assign(__assign({}, appliedFilters), { page: page, limit: PAGE_SIZE }));
    }, { keepPreviousData: true }), data = _r.data, isLoading = _r.isLoading, isFetching = _r.isFetching, error = _r.error;
    var totalRecords = (data === null || data === void 0 ? void 0 : data.total) || 0;
    var totalPages = (data === null || data === void 0 ? void 0 : data.totalPages) || 0;
    var toggleFilterPane = useCallback(function () {
        setFilterPaneIsVisible(function (prev) { return !prev; });
    }, []);
    var goToPage = useCallback(function (newPage) {
        setPage(newPage);
    }, []);
    return {
        // ListState interface for SearchLayout
        query: query,
        setQuery: setQuery,
        handleSearch: handleSearch,
        handleClearSearch: handleClearSearch,
        filterPaneIsVisible: filterPaneIsVisible,
        toggleFilterPane: toggleFilterPane,
        totalRecords: totalRecords,
        // Extended
        filters: {
            actionType: actionType,
            entityType: entityType,
            userId: userId,
            dateFrom: dateFrom,
            dateTo: dateTo,
        },
        setActionType: setActionType,
        setEntityType: setEntityType,
        setUserId: setUserId,
        userName: userName,
        setUserName: setUserName,
        setDateFrom: setDateFrom,
        setDateTo: setDateTo,
        dateValidationError: dateValidationError,
        handleClearFilters: handleClearFilters,
        isDefaultFilters: isDefaultFilters,
        items: (data === null || data === void 0 ? void 0 : data.data) || [],
        page: page,
        totalPages: totalPages,
        pageSize: PAGE_SIZE,
        isLoading: isLoading,
        isFetching: isFetching,
        error: error,
        goToPage: goToPage,
        appliedFilters: appliedFilters,
    };
};
export default useAuditLogList;
